import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ConfigService, LanguageService } from '@app/services';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { map, of, switchMap } from 'rxjs';
import { resolveImageUrl$ } from '../image/image-url';
import { getInteractionPromptIndicatorUri, getInteractionPromptRepeatDelay, getInteractionPromptText } from './interaction-prompt';
import { InteractionPromptPlayback } from './interaction-prompt-playback';

type TouchIndicatorLoad =
  | { type: 'default'; options: AnimationOptions }
  | { type: 'gif'; url: string };

@Component({
  selector: 'app-interaction-prompt',
  templateUrl: './interaction-prompt.component.html',
  styleUrl: './interaction-prompt.component.scss',
  imports: [LottieComponent],
  host: { '[class.static]': 'staticPrompt()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractionPromptComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly configService = inject(ConfigService);
  private readonly languageService = inject(LanguageService);
  private readonly mediaQuery = inject(DOCUMENT).defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)');
  private readonly reducedMotion = signal(this.mediaQuery?.matches ?? false);
  private readonly playback = new InteractionPromptPlayback();
  private activeLoad?: TouchIndicatorLoad;

  private readonly config = toSignal(this.configService.config$.pipe(map(config => config.navigation.interactionPrompt)));
  private readonly language = toSignal(this.languageService.currentLanguage$, { initialValue: 'en-CA' });
  private readonly indicatorType = computed(() => this.config()?.touchIndicator?.type ?? 'default');
  private readonly indicatorUri = computed(() => getInteractionPromptIndicatorUri(this.config()?.touchIndicator, this.language()));
  private readonly resolvedIndicator = toSignal(toObservable(this.indicatorUri).pipe(
    switchMap(uri => resolveImageUrl$(of(uri), this.configService.files$).pipe(map(url => ({ uri, url })))),
  ));

  protected readonly failedLoad = signal<TouchIndicatorLoad | undefined>(undefined);
  protected readonly loadedGif = signal<TouchIndicatorLoad | undefined>(undefined);
  protected readonly text = computed(() => getInteractionPromptText(this.config(), this.language()));
  protected readonly indicatorLoads = computed<TouchIndicatorLoad[]>(() => {
    if (this.reducedMotion()) return [];
    const uri = this.indicatorUri();
    const resolved = uri?.startsWith('file://') ? this.resolvedIndicator() : undefined;
    const path = uri?.startsWith('file://') ? (resolved?.uri === uri ? resolved.url : undefined) : uri;
    if (!path) return [];
    return this.indicatorType() === 'gif'
      ? [{ type: 'gif', url: path }]
      : [{ type: 'default', options: { path, loop: false, autoplay: false } }];
  });
  protected readonly staticPrompt = computed(() => {
    const load = this.indicatorLoads()[0];
    return !load || this.failedLoad() === load;
  });

  constructor() {
    effect(() => {
      this.playback.setRepeatDelay(getInteractionPromptRepeatDelay(this.config()));
      const load = this.indicatorLoads()[0];
      if (this.activeLoad !== load) {
        this.playback.clear();
        this.activeLoad = load;
      }
    });

    const updateMotion = (event: MediaQueryListEvent): void => this.reducedMotion.set(event.matches);
    this.mediaQuery?.addEventListener('change', updateMotion);
    this.destroyRef.onDestroy(() => {
      this.mediaQuery?.removeEventListener('change', updateMotion);
      this.playback.destroy();
    });
  }

  protected onAnimationCreated(animation: AnimationItem, load: TouchIndicatorLoad): void {
    if (this.destroyRef.destroyed || this.indicatorLoads()[0] !== load || load.type !== 'default') return;
    this.activeLoad = load;
    this.playback.attach(animation, () => this.failedLoad.set(load));
  }

  protected onGifLoaded(load: TouchIndicatorLoad): void {
    if (this.destroyRef.destroyed || this.indicatorLoads()[0] !== load || this.failedLoad() === load) return;
    this.loadedGif.set(load);
  }

  protected onGifError(load: TouchIndicatorLoad): void {
    if (this.destroyRef.destroyed || this.indicatorLoads()[0] !== load) return;
    this.failedLoad.set(load);
  }
}
